import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApDocumentType,
  ApJobSearchResult,
  ApProjectSearchResult,
  ApPurchaseDocument,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import { documentKey } from "./document-key";
import {
  AP_DOCUMENT_ENDPOINTS,
  agentRunsKey,
  documentPurchaseOrdersKey,
  reviewQueueKey,
} from "./keys";

/** A job, or a project for a document filed on the project itself with no job. */
export type ApMatchTarget =
  | { kind: "job"; job: ApJobSearchResult }
  | { kind: "project"; project: ApProjectSearchResult };

export const useMatchDocumentJob = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: { documentId: string; target: ApMatchTarget }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_job`,
          vars.target.kind === "job"
            ? { st_job_external_id: vars.target.job.id }
            : { st_project_external_id: vars.target.project.id },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: documentPurchaseOrdersKey(businessId, vars.documentId),
      });
    },
  });
};

export const useMatchLinePricebookItem = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: {
      documentId: string;
      lineItemId: string;
      pricebookItemId: string | null;
      applyToSameSkuLines?: boolean;
    }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_pricebook_item`,
          {
            line_item_id: vars.lineItemId,
            pricebook_item_id: vars.pricebookItemId,
            ...(vars.applyToSameSkuLines !== undefined && {
              apply_to_same_sku_lines: vars.applyToSameSkuLines,
            }),
          },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

export const useMatchDocumentPurchaseOrder = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: {
      documentId: string;
      stPurchaseOrderId: number | null;
    }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_purchase_order`,
          { st_purchase_order_id: vars.stPurchaseOrderId },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: documentPurchaseOrdersKey(businessId, vars.documentId),
      });
    },
  });
};

export const useMatchInvoiceJob = (businessId?: string | null) =>
  useMatchDocumentJob(businessId, "invoice");
